import _slicedToArray from "/vercel/path0/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "/vercel/path0/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _classCallCheck from "/vercel/path0/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/vercel/path0/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import { Buffer } from "buffer";
import AEAD from 'bcrypto/lib/js/aead';
import x25519 from 'bcrypto/lib/js/x25519';
import SHA256 from 'bcrypto/lib/js/sha256';
import { getHkdf } from "../utils";
import { logger } from "../logger";
export var MIN_NONCE = 0;
export var AbstractHandshake = /*#__PURE__*/function () {
  function AbstractHandshake() {
    _classCallCheck(this, AbstractHandshake);
  }

  _createClass(AbstractHandshake, [{
    key: "encryptWithAd",
    value: function encryptWithAd(cs, ad, plaintext) {
      var e = this.encrypt(cs.k, cs.n, ad, plaintext);
      this.setNonce(cs, this.incrementNonce(cs.n));
      return e;
    }
  }, {
    key: "decryptWithAd",
    value: function decryptWithAd(cs, ad, ciphertext) {
      var _this$decrypt = this.decrypt(cs.k, cs.n, ad, ciphertext),
          plaintext = _this$decrypt.plaintext,
          valid = _this$decrypt.valid;

      this.setNonce(cs, this.incrementNonce(cs.n));
      return {
        plaintext: plaintext,
        valid: valid
      };
    } // Cipher state related

  }, {
    key: "hasKey",
    value: function hasKey(cs) {
      return !this.isEmptyKey(cs.k);
    }
  }, {
    key: "setNonce",
    value: function setNonce(cs, nonce) {
      cs.n = nonce;
    }
  }, {
    key: "createEmptyKey",
    value: function createEmptyKey() {
      return Buffer.alloc(32);
    }
  }, {
    key: "isEmptyKey",
    value: function isEmptyKey(k) {
      var emptyKey = this.createEmptyKey();
      return emptyKey.equals(k);
    }
  }, {
    key: "incrementNonce",
    value: function incrementNonce(n) {
      return n + 1;
    }
  }, {
    key: "nonceToBytes",
    value: function nonceToBytes(n) {
      var nonce = Buffer.alloc(12);
      nonce.writeUInt32LE(n, 4);
      return nonce;
    }
  }, {
    key: "encrypt",
    value: function encrypt(k, n, ad, plaintext) {
      var nonce = this.nonceToBytes(n);
      var ctx = new AEAD();
      plaintext = Buffer.from(plaintext);
      ctx.init(k, nonce);
      ctx.aad(ad);
      ctx.encrypt(plaintext); // Encryption is done on the sent reference

      return Buffer.concat([plaintext, ctx.final()]);
    }
  }, {
    key: "encryptAndHash",
    value: function encryptAndHash(ss, plaintext) {
      var ciphertext;

      if (this.hasKey(ss.cs)) {
        ciphertext = this.encryptWithAd(ss.cs, ss.h, plaintext);
      } else {
        ciphertext = plaintext;
      }

      this.mixHash(ss, ciphertext);
      return ciphertext;
    }
  }, {
    key: "decrypt",
    value: function decrypt(k, n, ad, ciphertext) {
      var nonce = this.nonceToBytes(n);
      var ctx = new AEAD();
      ciphertext = Buffer.from(ciphertext);
      var tag = ciphertext.slice(ciphertext.length - 16);
      ciphertext = ciphertext.slice(0, ciphertext.length - 16);
      ctx.init(k, nonce);
      ctx.aad(ad);
      ctx.decrypt(ciphertext); // Decryption is done on the sent reference

      return {
        plaintext: ciphertext,
        valid: ctx.verify(tag)
      };
    }
  }, {
    key: "decryptAndHash",
    value: function decryptAndHash(ss, ciphertext) {
      var plaintext,
          valid = true;

      if (this.hasKey(ss.cs)) {
        var _this$decryptWithAd = this.decryptWithAd(ss.cs, ss.h, ciphertext);

        plaintext = _this$decryptWithAd.plaintext;
        valid = _this$decryptWithAd.valid;
      } else {
        plaintext = ciphertext;
      }

      this.mixHash(ss, ciphertext);
      return {
        plaintext: plaintext,
        valid: valid
      };
    }
  }, {
    key: "dh",
    value: function dh(privateKey, publicKey) {
      try {
        var derived = x25519.derive(publicKey, privateKey);
        var result = Buffer.alloc(32);
        derived.copy(result);
        return result;
      } catch (e) {
        logger(e.message);
        return Buffer.alloc(32);
      }
    }
  }, {
    key: "mixHash",
    value: function mixHash(ss, data) {
      ss.h = this.getHash(ss.h, data);
    }
  }, {
    key: "getHash",
    value: function getHash(a, b) {
      return SHA256.digest(Buffer.from([].concat(_toConsumableArray(a), _toConsumableArray(b))));
    }
  }, {
    key: "mixKey",
    value: function mixKey(ss, ikm) {
      var _getHkdf = getHkdf(ss.ck, ikm),
          _getHkdf2 = _slicedToArray(_getHkdf, 2),
          ck = _getHkdf2[0],
          tempK = _getHkdf2[1];

      ss.cs = this.initializeKey(tempK);
      ss.ck = ck;
    }
  }, {
    key: "initializeKey",
    value: function initializeKey(k) {
      var n = MIN_NONCE;
      return {
        k: k,
        n: n
      };
    } // Symmetric state related

  }, {
    key: "initializeSymmetric",
    value: function initializeSymmetric(protocolName) {
      var protocolNameBytes = Buffer.from(protocolName, 'utf-8');
      var h = this.hashProtocolName(protocolNameBytes);
      var ck = h;
      var key = this.createEmptyKey();
      var cs = this.initializeKey(key);
      return {
        cs: cs,
        ck: ck,
        h: h
      };
    }
  }, {
    key: "hashProtocolName",
    value: function hashProtocolName(protocolName) {
      if (protocolName.length <= 32) {
        var h = Buffer.alloc(32);
        protocolName.copy(h);
        return h;
      } else {
        return this.getHash(protocolName, Buffer.alloc(0));
      }
    }
  }, {
    key: "split",
    value: function split(ss) {
      var _getHkdf3 = getHkdf(ss.ck, Buffer.alloc(0)),
          _getHkdf4 = _slicedToArray(_getHkdf3, 2),
          tempk1 = _getHkdf4[0],
          tempk2 = _getHkdf4[1];

      var cs1 = this.initializeKey(tempk1);
      var cs2 = this.initializeKey(tempk2);
      return {
        cs1: cs1,
        cs2: cs2
      };
    }
  }, {
    key: "writeMessageRegular",
    value: function writeMessageRegular(cs, payload) {
      var ciphertext = this.encryptWithAd(cs, Buffer.alloc(0), payload);
      var ne = this.createEmptyKey();
      var ns = Buffer.alloc(0);
      return {
        ne: ne,
        ns: ns,
        ciphertext: ciphertext
      };
    }
  }, {
    key: "readMessageRegular",
    value: function readMessageRegular(cs, message) {
      return this.decryptWithAd(cs, Buffer.alloc(0), message.ciphertext);
    }
  }]);

  return AbstractHandshake;
}();